import {
  BalanceData, CatchReward, LevelModifiers, LevelSim, NO_MODIFIERS, OfferScreen, PET_ABILITY_SLOTS, PetAbilitySlot, PetData, PetTreatData, PetsState, PlayerProfile, QueuedUpgrade, UpgradeJob,
  UpgradeTarget, WildPetRule, applyCatch, wildPetFor, isPlainModifiers, petRule, purchasesAllowed, queueUpgrade, skinChanceBp, wildPetSkin, withoutPet,
} from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import { isNormalisedRun } from "./runProfile";
import { NOT_SAVING_HERE } from "./purchases";
import { OfflineSeedProvider } from "./seeds";
import type { LevelCatalog } from "./levelCatalog";

/**
 * Pets v2 as use cases (docs/PETS.md "Pets v2"): the `pets` save section (domain PetsState on the profile, read and
 * written with the catalog like Research and the supplies), the pet a battle takes and the wild encounter it may meet
 * (`prepareRun`: the run's PetRule and WildPetRule), what a run's wild pet settles (`settle`: the armed treat used up when
 * the pet appears, the catch paid once — a new pet, a level, or gold at the cap, and the skin it wore), the pet's
 * upgrades on the towers' upgrade system (`upgrade`: UpgradeTarget.Pet, its builders, queue, speed-ups and FINISH NOW),
 * the Pet Treats (bought for gems in the Shop, one armed on the battle card). The pet sheet's view is petsView.ts.
 * The caller saves after a change.
 */

/** The section, raw: PetsState migrates and validates it (a v1 section, Bond XP and derived ownership, reads as v2). */
export const petsSection = registerSaveSection<unknown>("pets", (v) => v, (v) => v);

/** Loads the section into the profile's pets (`highestCleared`: the save's, for the v1 migration). */
export function petsFromSave(save: HasSections, profile: PlayerProfile, highestCleared: number): void { profile.pets.restore(petsSection.read(save), highestCleared); }
export function petsToSave(save: HasSections, profile: PlayerProfile): void { petsSection.write(save, profile.pets.toJson()); }

/** The roster index of a pet (its `towerIndex` on UpgradeTarget.Pet jobs), or −1. */
export function petIndex(balance: BalanceData, petId: string): number { return balance.pets.list.findIndex((p) => p.id === petId); }

/** The name an upgrade job or queued upgrade shows: the Fortress, a tower type or a pet. */
export function upgradeTargetName(balance: BalanceData, target: UpgradeTarget, towerIndex: number): string {
  if (target === UpgradeTarget.Base) return "Fortress";
  if (target === UpgradeTarget.Pet) return balance.pets.list[towerIndex]?.name ?? "Pet";
  return balance.towers[towerIndex]?.name ?? "";
}

/** The key a run's wild pet is settled under (its catch, its treat): the level and the battle's seed. */
export function wildPetKey(sim: LevelSim): string { return `${sim.levelNumber}:${sim.seed.toString(16)}`; }

/** What a run's wild pet paid (Pets.settle). `fresh` false: paid already (a resumed or retried battle, a redraw). */
export interface CatchGrant {
  readonly reward: CatchReward;
  /** The skin it wore (a catalogue id), and whether the catch added it (false: owned already). */
  readonly skin: string | null;
  readonly skinNew: boolean;
  readonly fresh: boolean;
}

/** What `settle` found: the treat it used up now, and the catch's grant (null until the pet is caught). */
export interface PetSettlement { readonly treatUsed: string | null; readonly grant: CatchGrant | null }

/** The battle card's / Shop's treat row. */
export interface TreatSlot { readonly treat: PetTreatData; readonly have: number; readonly armed: boolean; readonly skinChancePercent: number }

/** The battle card's chip for one pet: its level against the cap, whether it comes along, the abilities unlocked. */
export interface PetCard { readonly pet: PetData; readonly level: number; readonly cap: number; readonly equipped: boolean; readonly unlocked: readonly PetAbilitySlot[] }

/** Grants kept for the end screen's redraws. */
const GRANTS_KEPT = 8;

export class Pets {
  private readonly grants = new Map<string, CatchGrant>();

  constructor(private readonly catalog: LevelCatalog) {}

  get state(): PetsState { return this.catalog.profile.pets; }
  private get balance(): BalanceData { return this.catalog.balance; }
  private get profile(): PlayerProfile { return this.catalog.profile; }

  owned(): readonly PetData[] { return this.state.owned(); }
  equipped(): PetData | null { return this.state.equipped(); }
  equip(id: string | null): boolean { return this.state.equip(id); }
  levelOf(id: string): number { return this.state.levelOf(id); }
  /** A pet's level never passes this (the highest tower level). */
  get levelCap(): number { return this.profile.petLevelCap; }

  card(pet: PetData): PetCard {
    const level = this.state.levelOf(pet.id);
    return { pet, level, cap: this.levelCap, equipped: this.equipped()?.id === pet.id, unlocked: PET_ABILITY_SLOTS.filter((a) => level >= pet.abilities[a].fromLevel) };
  }

  // ---- the battle

  /**
   * A new battle on `level` with `modifiers` (null: a plain campaign run): its run modifiers with the equipped pet
   * (at its level; even-footing runs at the data's level) and, on a campaign run from `wild.fromLevel` (first clears,
   * replays, Heroic, Trial; never Iron, Endless, Daily Road, challenge codes or event modes), the wild encounter with the
   * armed treat's chance. A RETRY's modifiers come in with the lost run's pet and wild pet, which are dropped first.
   */
  prepareRun(level: number, modifiers: LevelModifiers | null, seed?: bigint): LevelModifiers | null {
    const rest = modifiers === null ? null : withoutPet(modifiers);
    let out: LevelModifiers | null = rest === null || isPlainModifiers(rest) ? null : rest;
    const pet = this.equipped();
    if (pet !== null) out = { ...(out ?? NO_MODIFIERS), pet: petRule(this.balance, pet, this.state.levelOf(pet.id), isNormalisedRun(this.balance, modifiers)) };
    const wild = this.wildRule(level, rest, seed ?? this.attemptSeed(level));
    if (wild !== null) out = { ...(out ?? NO_MODIFIERS), wildPet: wild };
    return out;
  }

  /**
   * The wild encounter a run of `level` with `modifiers` meets, or null (too early, or a mode without one). Which pet
   * (Pets v3, domain meta/petWild.ts): its debut, else the weighted draw from the run's seed and level — `seed` the
   * run's (default: the attempt's, as the level flow derives it once the attempt has begun).
   */
  wildRule(level: number, modifiers: LevelModifiers | null, seed: bigint = this.attemptSeed(level)): WildPetRule | null {
    const B = this.balance, m = modifiers;
    if (level < B.pets.wild.fromLevel || m?.iron || m?.endless || m?.daily || m?.event || m?.challenge) return null;
    const pet = wildPetFor(B, level, seed, (id) => this.state.owns(id));
    if (pet === null) return null;
    const skins = B.commerce.shop.skins.list.filter((s) => s.kind === "pet" && s.pet === pet.id).map((s) => s.id);
    const lacking = skins.filter((id) => !this.catalog.ownsSkin(id));
    const treat = this.state.armedTreat();
    const rule: WildPetRule = { id: pet.id, skinChanceBp: skinChanceBp(B.pets, treat), skins: lacking.length > 0 ? lacking : skins, treat: treat?.id ?? "" };
    return this.state.owns(pet.id) ? rule : { ...rule, firstCatch: true };
  }

  /**
   * Settles a run's wild pet (call it whenever its events come, and at the end; idempotent per run, keyed by
   * `wildPetKey`): once it has appeared, the treat armed for it is used up; once it is caught, the catch is paid
   * (meta/petCatch.ts applyCatch: a new pet, +1 level, or gold at the cap) and the skin it wore is unlocked. A resumed
   * or checkpoint-retried battle replays the same encounter and pays nothing again (`fresh: false`).
   */
  settle(sim: LevelSim, now: number): PetSettlement {
    const rule = sim.mods.wildPet, w = sim.wildPet;
    if (rule === null || w === null) return { treatUsed: null, grant: null };
    const key = wildPetKey(sim);
    const appeared = w.phase !== "pending" && w.phase !== "waiting";
    const treatUsed = appeared && rule.treat !== "" && this.state.useTreat(rule.treat, key) ? rule.treat : null;
    if (w.phase !== "caught") return { treatUsed, grant: null };
    const known = this.grants.get(key);
    if (known) return { treatUsed, grant: { ...known, fresh: false } };
    if (!this.state.noteCatch(key)) return { treatUsed, grant: null };
    const reward = applyCatch(this.profile, rule.id, now);
    if (reward === null) return { treatUsed, grant: null };
    const skin = wildPetSkin(sim), skinNew = skin !== null && !this.catalog.ownsSkin(skin);
    if (skin !== null && skinNew) this.catalog.unlockSkin(skin);
    const grant: CatchGrant = { reward, skin, skinNew, fresh: true };
    this.grants.set(key, grant);
    if (this.grants.size > GRANTS_KEPT) this.grants.delete(this.grants.keys().next().value!);
    return { treatUsed, grant };
  }

  /** The seed of the attempt of `level` begun last (OfflineSeedProvider's: the player, the level, the attempt). */
  private attemptSeed(level: number): bigint { return new OfflineSeedProvider().levelSeed(this.catalog.playerId, level, this.catalog.attemptsOf(level)); }

  // ---- upgrades (the towers' system)

  /** Starts the pet's next upgrade, or queues it when every builder is busy or it is already upgrading; or why not. */
  upgrade(petId: string, now: number): UpgradeJob | QueuedUpgrade | string {
    const i = petIndex(this.balance, petId);
    if (i < 0 || !this.state.owns(petId)) return "no such pet";
    const p = this.profile;
    if (p.freeBuilders > 0 && !p.isUpgrading(UpgradeTarget.Pet, i)) return p.startUpgrade(UpgradeTarget.Pet, i, now);
    return queueUpgrade(p, UpgradeTarget.Pet, i, now);
  }

  /** The running upgrade on a pet, or null. */
  job(petId: string): UpgradeJob | null {
    const i = petIndex(this.balance, petId);
    return this.profile.jobs.find((j) => j.target === UpgradeTarget.Pet && j.towerIndex === i) ?? null;
  }

  // ---- Pet Treats

  treats(): TreatSlot[] {
    const armed = this.state.armedTreat()?.id ?? null, P = this.balance.pets;
    return P.treats.map((treat) => ({ treat, have: this.state.treatCount(treat.id), armed: armed === treat.id, skinChancePercent: skinChanceBp(P, treat) / 100 }));
  }
  /** The battle card's tap: arm a held treat (replacing another), or disarm (the armed one, or null). */
  toggleTreat(id: string | null): ReturnType<PetsState["toggleTreat"]> { return this.state.toggleTreat(id); }

  /** Buys one Pet Treat for gems: the hub's Shop only, never while the save cannot be written, refused at the cap. */
  buyTreat(id: string, now: number, screen: OfferScreen = "hub"): true | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    const t = this.balance.pets.treats.find((x) => x.id === id);
    if (t === undefined) return "unknown treat";
    if (this.state.treatCount(id) >= this.balance.pets.treatMaxOwned) return "you hold all you can carry";
    const paid = this.catalog.economy.wallet.spend("gems", t.gems, "petTreat", now, id);
    if (paid !== true) return paid;
    this.state.addTreat(id, 1);
    return true;
  }
}
